import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { PostHogProvider } from '@posthog/react'
import * as Sentry from '@sentry/react'
import { queryClient } from '@/utils/main-variables'
import { cleanupExpiredTempLogin } from '@/utils/temp-login'
import { FontProvider } from './context/font-context'
import { ThemeProvider } from './context/theme-context'
import './index.css'
import './utils/i18n/i18n.ts'
import { router } from './utils/main-variables'

// Generated Routes

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn && sentryDsn !== 'VITE_SENTRY_DSN') {
  Sentry.init({
    dsn: sentryDsn,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  })
}

// Clean up expired temporary login tokens on app start
cleanupExpiredTempLogin()

// Render the app
const rootElement = document.getElementById('root')!

// Color environment variables
const colors = {
  light: {
    background: import.meta.env.VITE_BACKGROUND_LIGHT,
    foreground: import.meta.env.VITE_FOREGROUND_LIGHT,
    card: import.meta.env.VITE_CARD_LIGHT,
    cardForeground: import.meta.env.VITE_CARD_FOREGROUND_LIGHT,
    popover: import.meta.env.VITE_POPOVER_LIGHT,
    popoverForeground: import.meta.env.VITE_POPOVER_FOREGROUND_LIGHT,
    primary: import.meta.env.VITE_PRIMARY_LIGHT,
    primaryForeground: import.meta.env.VITE_PRIMARY_FOREGROUND_LIGHT,
    secondary: import.meta.env.VITE_SECONDARY_LIGHT,
    secondaryForeground: import.meta.env.VITE_SECONDARY_FOREGROUND_LIGHT,
    muted: import.meta.env.VITE_MUTED_LIGHT,
    mutedForeground: import.meta.env.VITE_MUTED_FOREGROUND_LIGHT,
    accent: import.meta.env.VITE_ACCENT_LIGHT,
    accentForeground: import.meta.env.VITE_ACCENT_FOREGROUND_LIGHT,
    destructive: import.meta.env.VITE_DESTRUCTIVE_LIGHT,
    destructiveForeground: import.meta.env.VITE_DESTRUCTIVE_FOREGROUND_LIGHT,
    border: import.meta.env.VITE_BORDER_LIGHT,
    input: import.meta.env.VITE_INPUT_LIGHT,
    ring: import.meta.env.VITE_RING_LIGHT,
    chart1: import.meta.env.VITE_CHART_1_LIGHT,
    chart2: import.meta.env.VITE_CHART_2_LIGHT,
    chart3: import.meta.env.VITE_CHART_3_LIGHT,
    chart4: import.meta.env.VITE_CHART_4_LIGHT,
    chart5: import.meta.env.VITE_CHART_5_LIGHT,
    sidebar: import.meta.env.VITE_SIDEBAR_LIGHT,
    sidebarForeground: import.meta.env.VITE_SIDEBAR_FOREGROUND_LIGHT,
    sidebarPrimary: import.meta.env.VITE_SIDEBAR_PRIMARY_LIGHT,
    sidebarPrimaryForeground: import.meta.env
      .VITE_SIDEBAR_PRIMARY_FOREGROUND_LIGHT,
    sidebarAccent: import.meta.env.VITE_SIDEBAR_ACCENT_LIGHT,
    sidebarAccentForeground: import.meta.env
      .VITE_SIDEBAR_ACCENT_FOREGROUND_LIGHT,
    sidebarBorder: import.meta.env.VITE_SIDEBAR_BORDER_LIGHT,
    sidebarRing: import.meta.env.VITE_SIDEBAR_RING_LIGHT,
  },
  dark: {
    background: import.meta.env.VITE_BACKGROUND_DARK,
    foreground: import.meta.env.VITE_FOREGROUND_DARK,
    card: import.meta.env.VITE_CARD_DARK,
    cardForeground: import.meta.env.VITE_CARD_FOREGROUND_DARK,
    popover: import.meta.env.VITE_POPOVER_DARK,
    popoverForeground: import.meta.env.VITE_POPOVER_FOREGROUND_DARK,
    primary: import.meta.env.VITE_PRIMARY_DARK,
    primaryForeground: import.meta.env.VITE_PRIMARY_FOREGROUND_DARK,
    secondary: import.meta.env.VITE_SECONDARY_DARK,
    secondaryForeground: import.meta.env.VITE_SECONDARY_FOREGROUND_DARK,
    muted: import.meta.env.VITE_MUTED_DARK,
    mutedForeground: import.meta.env.VITE_MUTED_FOREGROUND_DARK,
    accent: import.meta.env.VITE_ACCENT_DARK,
    accentForeground: import.meta.env.VITE_ACCENT_FOREGROUND_DARK,
    destructive: import.meta.env.VITE_DESTRUCTIVE_DARK,
    destructiveForeground: import.meta.env.VITE_DESTRUCTIVE_FOREGROUND_DARK,
    border: import.meta.env.VITE_BORDER_DARK,
    input: import.meta.env.VITE_INPUT_DARK,
    ring: import.meta.env.VITE_RING_DARK,
    chart1: import.meta.env.VITE_CHART_1_DARK,
    chart2: import.meta.env.VITE_CHART_2_DARK,
    chart3: import.meta.env.VITE_CHART_3_DARK,
    chart4: import.meta.env.VITE_CHART_4_DARK,
    chart5: import.meta.env.VITE_CHART_5_DARK,
    sidebar: import.meta.env.VITE_SIDEBAR_DARK,
    sidebarForeground: import.meta.env.VITE_SIDEBAR_FOREGROUND_DARK,
    sidebarPrimary: import.meta.env.VITE_SIDEBAR_PRIMARY_DARK,
    sidebarPrimaryForeground: import.meta.env
      .VITE_SIDEBAR_PRIMARY_FOREGROUND_DARK,
    sidebarAccent: import.meta.env.VITE_SIDEBAR_ACCENT_DARK,
    sidebarAccentForeground: import.meta.env
      .VITE_SIDEBAR_ACCENT_FOREGROUND_DARK,
    sidebarBorder: import.meta.env.VITE_SIDEBAR_BORDER_DARK,
    sidebarRing: import.meta.env.VITE_SIDEBAR_RING_DARK,
  },
}

function applyPrimaryColor() {
  const isDark = document.documentElement.classList.contains('dark')
  const theme = isDark ? colors.dark : colors.light

  // Apply all color CSS variables if they are defined in environment
  const colorMap = {
    '--background': theme.background,
    '--foreground': theme.foreground,
    '--card': theme.card,
    '--card-foreground': theme.cardForeground,
    '--popover': theme.popover,
    '--popover-foreground': theme.popoverForeground,
    '--primary': theme.primary,
    '--primary-foreground': theme.primaryForeground,
    '--secondary': theme.secondary,
    '--secondary-foreground': theme.secondaryForeground,
    '--muted': theme.muted,
    '--muted-foreground': theme.mutedForeground,
    '--accent': theme.accent,
    '--accent-foreground': theme.accentForeground,
    '--destructive': theme.destructive,
    '--destructive-foreground': theme.destructiveForeground,
    '--border': theme.border,
    '--input': theme.input,
    '--ring': theme.ring,
    '--chart-1': theme.chart1,
    '--chart-2': theme.chart2,
    '--chart-3': theme.chart3,
    '--chart-4': theme.chart4,
    '--chart-5': theme.chart5,
    '--sidebar': theme.sidebar,
    '--sidebar-foreground': theme.sidebarForeground,
    '--sidebar-primary': theme.sidebarPrimary,
    '--sidebar-primary-foreground': theme.sidebarPrimaryForeground,
    '--sidebar-accent': theme.sidebarAccent,
    '--sidebar-accent-foreground': theme.sidebarAccentForeground,
    '--sidebar-border': theme.sidebarBorder,
    '--sidebar-ring': theme.sidebarRing,
  }

  Object.entries(colorMap).forEach(([cssVar, value]) => {
    if (value) {
      document.documentElement.style.setProperty(cssVar, value)
    }
  })
}

// apply on load
applyPrimaryColor()

// also apply again when user toggles theme
const observer = new MutationObserver(applyPrimaryColor)
observer.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['class'],
})

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
} as const

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)

  root.render(
    <StrictMode>
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={options}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme='light' storageKey='vite-ui-theme'>
            <FontProvider>
              <RouterProvider router={router} />
            </FontProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>
  )
}
